import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { askPulseAI } from "@/lib/pulse/pulseAI";
import { isAIRoutedIntent } from "@/lib/pulse/queryRouter";

export type PulseEntityType = "player" | "match" | "session" | "squad" | null;

export interface PulseContext {
  currentRoute: string;
  entityType: PulseEntityType;
  entityId: string | null;
  entityName: string | null;
  organizationId: string;
  userRole: string;
  userId: string;
}

export interface PulseAction {
  actionId: string;
  label: string;
  actionType: string;
  payload: Record<string, unknown>;
  requiresConfirmation: boolean;
  guardrailKey?: string;
}

export type PulseAnswerType = "data" | "action" | "clarification" | "error";
export type PulseConfidence = "high" | "medium" | "low" | "no_data";

export interface PulseResponse {
  answer: string;
  answerType: PulseAnswerType;
  confidence: PulseConfidence;
  domain: string;
  intent: string;
  dataPoints: Record<string, unknown> | null;
  actions: PulseAction[] | null;
  followUpSuggestions: string[];
}

function safeErrorResponse(message: string): PulseResponse {
  return {
    answer: message,
    answerType: "error",
    confidence: "no_data",
    domain: "unknown",
    intent: "error",
    dataPoints: null,
    actions: null,
    followUpSuggestions: [],
  };
}

export function buildPulseContext(
  currentRoute: string,
  entityType: PulseEntityType,
  entityId: string | null,
  entityName: string | null,
  organizationId: string,
  userRole: string,
  userId: string,
): PulseContext {
  return {
    currentRoute,
    entityType,
    entityId,
    entityName,
    organizationId,
    userRole,
    userId,
  };
}

// Short follow-up tokens that should always be treated as continuations of
// the previous Pulse turn — never as a fresh standalone query.
const CONTINUATION_WORDS = new Set([
  "ja", "nein", "yes", "no", "y", "n",
  "oui", "non", "sí", "si", "okay", "ok", "sure",
  "go ahead", "tell me more", "more", "details", "weiter",
  "zeig mir", "zeig mir mehr", "mehr", "more please",
  "please", "bitte", "danke", "thanks",
]);

function isContinuation(message: string): boolean {
  const trimmed = message.trim().toLowerCase().replace(/[?.!,]+$/g, "");
  if (!trimmed) return false;
  if (trimmed.length < 10) return true;
  return CONTINUATION_WORDS.has(trimmed);
}

const lastResponseKey = (orgId: string | null | undefined) =>
  `pulse_last_response_${orgId ?? "default"}`;

export function usePulse() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastPulseResponse, setLastPulseResponseState] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      // Best-effort hydration without orgId; the org-scoped value is
      // re-read inside sendMessage when context.organizationId is known.
      const keys = Object.keys(sessionStorage).filter((k) => k.startsWith("pulse_last_response_"));
      if (keys.length === 0) return null;
      return sessionStorage.getItem(keys[0]);
    } catch {
      return null;
    }
  });

  const persistLastResponse = useCallback((orgId: string | null | undefined, value: string | null) => {
    setLastPulseResponseState(value);
    if (typeof window === "undefined") return;
    try {
      if (value) sessionStorage.setItem(lastResponseKey(orgId), value);
      else sessionStorage.removeItem(lastResponseKey(orgId));
    } catch { /* ignore */ }
  }, []);

  const sendMessage = useCallback(
    async (message: string, context: PulseContext): Promise<PulseResponse> => {
      setIsLoading(true);
      setError(null);

      const continuation = isContinuation(message) && !!lastPulseResponse;

      // Helper: call Claude (pulse-query) with the given intent. "auto" tells
      // the edge function to fetch every bundle and let Claude infer intent.
      const callAI = async (intent: string, fallbackDomain = "auto"): Promise<PulseResponse | null> => {
        try {
          const ai = await askPulseAI(message, context, intent, continuation ? lastPulseResponse : null);
          if (ai.ruleBased || !ai.answer) return null;
          return {
            answer: ai.answer,
            answerType: "data",
            confidence: ai.confidence,
            domain: ai.domain || fallbackDomain,
            intent: ai.intent || intent,
            dataPoints: ai.dataPoints,
            actions: null,
            followUpSuggestions: ai.followUpSuggestions ?? [],
          };
        } catch (aiErr) {
          console.warn("[pulse] Claude call failed", aiErr);
          return null;
        }
      };

      // On first send for this org, prefer the org-scoped sessionStorage value
      // over whatever was hydrated generically at mount time.
      if (typeof window !== "undefined") {
        try {
          const scoped = sessionStorage.getItem(lastResponseKey(context.organizationId));
          if (scoped && scoped !== lastPulseResponse) setLastPulseResponseState(scoped);
        } catch { /* ignore */ }
      }

      const persist = (res: PulseResponse): PulseResponse => {
        if (res.answerType === "data" || res.answerType === "clarification") {
          persistLastResponse(context.organizationId, res.answer);
        }
        return res;
      };

      try {
        // Continuation messages bypass the keyword classifier entirely —
        // they only make sense in the context of the previous Pulse turn.
        if (continuation) {
          const ai = await callAI("auto");
          if (ai) return persist(ai);
          return persist(safeErrorResponse(
            "The AI assistant is temporarily unavailable. Please try again in a moment.",
          ));
        }

        // 1. Classifier fast-path. Always run it so action intents and
        // high-confidence keyword matches stay deterministic.
        const { data, error: invokeError } = await supabase.functions.invoke("pulse", {
          body: { message, context },
        });

        if (invokeError || !data || typeof data !== "object") {
          // Classifier itself failed — go straight to Claude.
          const ai = await callAI("auto");
          if (ai) return persist(ai);
          const msg = invokeError?.message || "Unable to reach the assistant. Please try again.";
          setError(msg);
          return safeErrorResponse(msg);
        }

        const ruleBased = data as PulseResponse;

        if (ruleBased.answerType === "action") {
          return ruleBased;
        }

        if (ruleBased.confidence === "high" && ruleBased.answerType !== "clarification") {
          return persist(ruleBased);
        }

        const ai = await callAI("auto", ruleBased.domain);
        if (ai) return persist(ai);

        if (ruleBased.answerType === "clarification" || ruleBased.confidence === "no_data") {
          return safeErrorResponse(
            "The AI assistant is temporarily unavailable. Please try again in a moment.",
          );
        }
        return persist(ruleBased);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Unexpected error contacting the assistant.";
        setError(msg);
        return safeErrorResponse(msg);
      } finally {
        setIsLoading(false);
      }
    },
    [lastPulseResponse],
  );

  return { sendMessage, isLoading, error };
}
